// frontend/src/pages/reportpage/SharesReportTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { exportToCSV } from './reportUtils';
import globalApi from '../../utils/globalFetchWrapper';

const SharesReportTab = ({
  isLoading,
  sharesReport,
  sharesSearch,
  setSharesSearch,
}) => {
  // ── Sub-tab: Active Members (existing report) vs Left Members (audit trail) ──
  const [subTab, setSubTab] = useState('active'); // 'active' | 'left'
  const [leftMembers, setLeftMembers] = useState([]);
  const [leftLoading, setLeftLoading] = useState(false);
  const [leftError, setLeftError] = useState('');
  const [leftFetched, setLeftFetched] = useState(false);
  const [leftSearch, setLeftSearch] = useState('');
  const [expandedLeftMemberId, setExpandedLeftMemberId] = useState(null);

  const fetchLeftMembers = useCallback(async () => {
    setLeftLoading(true);
    setLeftError('');
    try {
      const response = await globalApi.get('/members/left');

      if (response.status === 429) {
        setLeftError('Rate limited - please wait before refreshing');
        setLeftLoading(false);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setLeftMembers(data.data?.members || []);
        setLeftFetched(true);
      } else {
        setLeftError(data.message || 'Failed to load left members');
      }
    } catch (err) {
      if (err.message === 'Session expired') return;
      console.error('Error:', err);
      setLeftError('Failed to fetch left members');
    } finally {
      setLeftLoading(false);
    }
  }, []);

  useEffect(() => {
    if (subTab === 'left' && !leftFetched) {
      fetchLeftMembers();
    }
  }, [subTab, leftFetched, fetchLeftMembers]);

  const filteredLeftMembers = leftMembers.filter((m) => {
    const q = leftSearch.toLowerCase();
    return !q || (m.memberName || '').toLowerCase().includes(q) || (m.memberId || '').toLowerCase().includes(q);
  });

  // Cross-check totals computed directly from the left-members list itself —
  // should match sharesReport.summary.retainedShareCapitalFromExits.
  const retainedFromList = leftMembers
    .filter((m) => m.shareCapitalTransferType === 'retain' && m.shareCapitalAmount > 0)
    .reduce((s, m) => s + (m.shareCapitalAmount || 0), 0);
  const transferredFromList = leftMembers
    .filter((m) => m.shareCapitalTransferType === 'transfer' && m.shareCapitalAmount > 0)
    .reduce((s, m) => s + (m.shareCapitalAmount || 0), 0);

  const exportLeftMembersCSV = () => {
    const dataToExport = leftMembers.map((m) => ({
      'Member ID': m.memberId,
      Name: m.memberName,
      'Left Date': m.leftDate ? new Date(m.leftDate).toISOString().slice(0, 10) : '',
      'Shares At Exit': m.sharesAtExit || 0,
      Refunded: m.sharesRefunded || 0,
      'Share Capital (KES)': m.shareCapitalAmount || 0,
      'Capital Status': m.shareCapitalTransferType === 'transfer'
        ? `Transferred to ${m.shareCapitalBoughtByName || m.shareCapitalBoughtBy}`
        : m.shareCapitalTransferType === 'retain'
          ? 'Retained in SACCO'
          : 'None',
      Reason: m.leftReason || '',
      'Processed By': m.exitProcessedBy || '',
    }));
    exportToCSV(dataToExport, 'left-members-share-capital.csv');
  };

  return (
    <div className="shares-tab">
      {isLoading ? (
        <div className="loading">Loading shares report...</div>
      ) : sharesReport ? (
        <div className="report-content">
          <div className="report-title-row">
            <h3>Shares Report</h3>
            <div className="btn-group">
              <button className="btn btn-outline btn-sm" onClick={() => window.print()}>
                🖨️ Print
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() =>
                  subTab === 'left'
                    ? exportLeftMembersCSV()
                    : exportToCSV(sharesReport.members, `shares-report.csv`)
                }
              >
                ⤓ Export CSV
              </button>
            </div>
          </div>

          <div className="btn-group" style={{ margin: '12px 0' }}>
            <button
              className={`btn btn-sm ${subTab === 'active' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setSubTab('active')}
            >
              👥 Active Members
            </button>
            <button
              className={`btn btn-sm ${subTab === 'left' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setSubTab('left')}
            >
              🗂️ Left Members (Audit)
            </button>
          </div>

          {subTab === 'active' && (
          <>
          {sharesReport.summary.reportMonthLabel && (
            <div style={{ marginBottom: 10, padding: '8px 14px', background: '#f0f0ff', borderRadius: 6, fontSize: 13, color: '#4b3ff6', fontWeight: 600, borderLeft: '4px solid #4b3ff6' }}>
              📅 Balances shown as at end of {sharesReport.summary.reportMonthLabel}
            </div>
          )}

          <div className="compact-summary">
            <div className="compact-summary-item">
              <span className="compact-label">Members:</span>
              <span className="compact-value">{sharesReport.summary.totalMembers}</span>
            </div>
            <div className="compact-summary-item">
              <span className="compact-label">Capital Shares:</span>
              <span className="compact-value">KES {sharesReport.summary.totalCapitalShares?.toLocaleString()}</span>
            </div>
            <div className="compact-summary-item">
              <span className="compact-label">Available Shares:</span>
              <span className="compact-value">KES {sharesReport.summary.totalAvailableShares?.toLocaleString()}</span>
            </div>
            <div className="compact-summary-item highlight">
              <span className="compact-label">Total:</span>
              <span className="compact-value text-success">KES {sharesReport.summary.totalShares?.toLocaleString()}</span>
            </div>
            {sharesReport.summary.retainedShareCapitalFromExits > 0 && (
              <div className="compact-summary-item">
                <span className="compact-label">+ Retained from Exits:</span>
                <span className="compact-value">KES {sharesReport.summary.retainedShareCapitalFromExits?.toLocaleString()}</span>
              </div>
            )}
            {sharesReport.summary.retainedShareCapitalFromExits > 0 && (
              <div className="compact-summary-item highlight">
                <span className="compact-label">Total SACCO Share Assets:</span>
                <span className="compact-value text-success">KES {sharesReport.summary.totalSaccoShareAssets?.toLocaleString()}</span>
              </div>
            )}
            <div className="compact-summary-item">
              <span className="compact-label">Avg/Member:</span>
              <span className="compact-value">KES {Number(sharesReport.summary.averageSharesPerMember || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            </div>
            {sharesReport.summary.totalCurrentMonthContributions > 0 && (
              <div className="compact-summary-item">
                <span className="compact-label">This Month Total:</span>
                <span className="compact-value">KES {sharesReport.summary.totalCurrentMonthContributions?.toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Search / filter */}
          <div style={{ margin: '12px 0', display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="🔍  Search by name or Member ID..."
              value={sharesSearch}
              onChange={e => setSharesSearch(e.target.value)}
              style={{ padding: '7px 12px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, minWidth: 260 }}
            />
            {sharesSearch && (
              <button onClick={() => setSharesSearch('')} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #ccc', cursor: 'pointer', background: '#f5f5f5' }}>
                ✕ Clear
              </button>
            )}
            <span style={{ fontSize: 13, color: '#888' }}>
              {sharesReport.members.filter(m => {
                const q = sharesSearch.toLowerCase();
                return !q || (m.memberName || '').toLowerCase().includes(q) || (m.memberId || '').toLowerCase().includes(q);
              }).length} of {sharesReport.members.length} members
            </span>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member ID</th>
                  <th>Name</th>
                  <th className="text-right">Total Shares (KES)</th>
                  <th className="text-right">{sharesReport.summary.reportMonthLabel ? sharesReport.summary.reportMonthLabel.split(' ')[0] : 'This Month'} Contri. (KES)</th>
                  <th className="text-right">Capital (KES)</th>
                  <th className="text-right">Available (KES)</th>
                </tr>
              </thead>
              <tbody>
                {sharesReport.members
                  .filter(m => {
                    const q = sharesSearch.toLowerCase();
                    return !q || (m.memberName || '').toLowerCase().includes(q) || (m.memberId || '').toLowerCase().includes(q);
                  })
                  .map((member, index) => (
                  <tr key={index}
                    onMouseEnter={e => e.currentTarget.style.background = '#bfdbfe'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td><strong>{member.memberId}</strong></td>
                    <td>{member.memberName}</td>
                    <td className="text-right"><strong>{Number(member.totalShares || 0).toLocaleString()}</strong></td>
                    <td className="text-right" style={{ color: member.currentMonthContribution > 0 ? '#16a34a' : '#999' }}>
                      {member.currentMonthContribution > 0 ? Number(member.currentMonthContribution).toLocaleString() : '—'}
                    </td>
                    <td className="text-right">{Number(member.capitalShares || 0).toLocaleString()}</td>
                    <td className="text-right">{Number(member.availableShares || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
          )}

          {subTab === 'left' && (
          <>
            <div style={{ marginBottom: 10, padding: '8px 14px', background: '#fff7ed', borderRadius: 6, fontSize: 13, color: '#9a3412', fontWeight: 600, borderLeft: '4px solid #f59e0b' }}>
              🗂️ Audit trail — share capital belonging to members who have exited the SACCO. Retained capital is real SACCO equity that no longer appears on any active member's row.
            </div>

            <div className="compact-summary">
              <div className="compact-summary-item">
                <span className="compact-label">Left Members:</span>
                <span className="compact-value">{leftMembers.length}</span>
              </div>
              <div className="compact-summary-item highlight">
                <span className="compact-label">Retained in SACCO:</span>
                <span className="compact-value text-success">KES {retainedFromList.toLocaleString()}</span>
              </div>
              <div className="compact-summary-item">
                <span className="compact-label">Transferred to Members:</span>
                <span className="compact-value">KES {transferredFromList.toLocaleString()}</span>
              </div>
              {sharesReport.summary.retainedShareCapitalFromExits > 0 &&
                sharesReport.summary.retainedShareCapitalFromExits !== retainedFromList && (
                <div className="compact-summary-item">
                  <span className="compact-label" style={{ color: '#dc2626' }}>⚠ Shares Report total shows:</span>
                  <span className="compact-value" style={{ color: '#dc2626' }}>KES {sharesReport.summary.retainedShareCapitalFromExits.toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* Search / filter */}
            <div style={{ margin: '12px 0', display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                type="text"
                placeholder="🔍  Search by name or Member ID..."
                value={leftSearch}
                onChange={e => setLeftSearch(e.target.value)}
                style={{ padding: '7px 12px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, minWidth: 260 }}
              />
              {leftSearch && (
                <button onClick={() => setLeftSearch('')} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #ccc', cursor: 'pointer', background: '#f5f5f5' }}>
                  ✕ Clear
                </button>
              )}
              <span style={{ fontSize: 13, color: '#888' }}>
                {filteredLeftMembers.length} of {leftMembers.length} left members
              </span>
            </div>

            {leftError && (
              <div className="alert alert-error" role="alert" style={{ marginBottom: 10 }}>{leftError}</div>
            )}

            {leftLoading ? (
              <div className="loading">Loading left members...</div>
            ) : leftMembers.length === 0 ? (
              <p className="table-note">No members have left the SACCO yet.</p>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Member ID</th>
                      <th>Name</th>
                      <th>Left Date</th>
                      <th className="text-right">Shares at Exit (KES)</th>
                      <th className="text-right">Refunded (KES)</th>
                      <th>Share Capital (KES 5,000)</th>
                      <th>Processed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeftMembers.map((m) => {
                      const isExpanded = expandedLeftMemberId === m.memberId;
                      return (
                        <React.Fragment key={m.memberId}>
                          <tr
                            onClick={() => setExpandedLeftMemberId(isExpanded ? null : m.memberId)}
                            style={{ cursor: 'pointer', background: isExpanded ? '#f0f0ff' : undefined }}
                            onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#bfdbfe'; }}
                            onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = ''; }}
                          >
                            <td><strong>{m.memberId}</strong></td>
                            <td>
                              {m.memberName}
                              <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>
                                {isExpanded ? '▲ collapse' : '▼ details'}
                              </span>
                            </td>
                            <td>{m.leftDate ? new Date(m.leftDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                            <td className="text-right">{Number(m.sharesAtExit || 0).toLocaleString()}</td>
                            <td className="text-right">{Number(m.sharesRefunded || 0).toLocaleString()}</td>
                            <td>
                              {m.shareCapitalTransferType === 'transfer'
                                ? <span style={{ color: '#10b981' }}>→ {m.shareCapitalBoughtByName || m.shareCapitalBoughtBy}</span>
                                : m.shareCapitalTransferType === 'none' || m.shareCapitalAmount === 0
                                ? <span style={{ color: '#6b7280' }}>None (no shares)</span>
                                : <span style={{ color: '#f59e0b' }}>Retained in SACCO</span>}
                            </td>
                            <td style={{ fontSize: 12, color: '#6b7280' }}>{m.exitProcessedBy || '—'}</td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={7} style={{ padding: 0, borderBottom: '2px solid #e0e0ff' }}>
                                <div style={{ background: '#f8f8ff', padding: '16px 20px', borderLeft: '4px solid #4b3ff6' }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 32px', fontSize: 13, marginBottom: 12 }}>
                                    {[
                                      ['Email', m.email || '—'],
                                      ['Phone', m.phoneNumber || '—'],
                                      ['Reason for Leaving', m.leftReason || '—'],
                                      ['Share Capital Status', m.shareCapitalTransferType === 'transfer'
                                        ? `Transferred to ${m.shareCapitalBoughtByName || m.shareCapitalBoughtBy} on ${m.shareCapitalTransferDate ? new Date(m.shareCapitalTransferDate).toLocaleDateString('en-GB') : '—'}`
                                        : m.shareCapitalAmount > 0 ? 'Retained in SACCO' : 'None (member had no shares)'],
                                    ].map(([label, value]) => (
                                      <div key={label}>
                                        <span style={{ color: '#6b7280', display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
                                        <span style={{ fontWeight: 500 }}>{value}</span>
                                      </div>
                                    ))}
                                  </div>
                                  {m.exitNotes && (
                                    <div style={{ fontSize: 12, color: '#6b7280', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px' }}>
                                      <strong>Exit Notes:</strong> {m.exitNotes}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default SharesReportTab;
